import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { BaseDraggable } from "../BaseDraggable";
import { useStore } from "../../../stores/store";
import { DraggableKeys, ModalKeys } from "../../../interfaces/config";
import PrintViewModel from "@arcgis/core/widgets/Print/PrintViewModel";
import esriConfig from "@arcgis/core/config";

import {
  getAutoTableOptions,
  printParcelDetails,
  getDataUrlFromArrayBuffer as getDataUrlFromArrayBuffer,
  mergePdfs,
  addDisclaimer,
  addLegendImage,
} from "./printHelpers";

import jsPDF from "jspdf";
import "jspdf-autotable";

const key = DraggableKeys.PRINT_DRAGGABLE;

export default function PrintDraggable() {
  const {
    isActive,
    toggleActiveItem,
    mapView,
    results,
    getMostCurrentDetailsByPin,
    activateTool,
    config,
    hideDataPrint,
  } = useStore();
  const [isPrinting, setIsPrinting] = useState(false);
  const [printTitle, setPrintTitle] = useState("");
  const [showLegend, setShowLegend] = useState(false);
  const [selectedPrintTemplate, setSelectedPrintTemplate] = useState(
    "Letter ANSI A Landscape"
  );
  const [preserveScale, setPreserveScale] = useState(true);
  const [selectedPrintFormat, setSelectedPrintFormat] = useState("pdf");
  const [selectedDataOption, setSelectedDataOption] = useState("summary");
  const [selectedPrintType, setSelectedPrintType] = useState("map-only");
  const [printoutUrl, setPrintoutUrl] = useState("");
  const doc = useRef<jsPDF | null>(null);

  useEffect(() => {
    if (results.length === 0) {
      setSelectedPrintType("map-only");
    }
  }, [results]);

  const generatePdf = useCallback(async () => {
    const orientation = selectedPrintTemplate.includes("Landscape") ? "l" : "p";
    doc.current = new jsPDF(orientation);
    let headerRow: string[] = [];
    let rows: any[] = [];
    if (config && config.printOptions.summaryExportFields) {
      headerRow = Object.keys(results[0].attributes)
        .filter((field) =>
          config.printOptions.summaryExportFields?.some(
            (f) => f.field === field
          )
        )
        .map((field) => {
          const fieldConfig = config?.printOptions.summaryExportFields?.find(
            (f) => f.field === field
          );
          return fieldConfig?.label || field;
        });

      rows = results.map((result) =>
        Object.keys(result.attributes)
          .filter((field) =>
            config.printOptions.summaryExportFields?.some(
              (f) => f.field === field
            )
          )
          .map((field) => result.attributes[field])
      );
    } else {
      headerRow = Object.keys(results[0].attributes).slice(0, 7);
      rows = results.map((result) =>
        Object.values(result.attributes).slice(0, 7)
      );
    }

    if (selectedDataOption === "summary") {
      doc.current.autoTable(
        getAutoTableOptions([headerRow], rows, {
          headerTextColor: config?.printOptions.headerTextColor,
          sectionHeaderTextColor: config?.printOptions.sectionHeaderTextColor,
          tableHeaderBackgroundColor:
            config?.printOptions.tableHeaderBackgroundColor,
          tableHeaderTextColor: config?.printOptions.tableHeaderTextColor,
        })
      );
      return doc.current?.output("arraybuffer");
    } else {
      const detailsPromises: any = [];
      const truncateValue = 100;
      const truncatedResults = results.slice(0, truncateValue);

      if (results.length > truncateValue) {
        activateTool(ModalKeys.MAX_PRINT_RESULTS_EXCEEDED);
      }

      for (let i = 0; i < truncatedResults.length; i++) {
        const result = truncatedResults[i].attributes;
        const details = getMostCurrentDetailsByPin(result.ParcelNumber);
        detailsPromises.push(details);
      }

      const detailResults = await Promise.all(detailsPromises);
      for (let i = 0; i < detailResults.length; i++) {
        const result = detailResults[i];
        let newStart = 10;
        if (i > 0) {
          newStart = doc.current.autoTable.previous.finalY + 10;
        }
        try {
          await printParcelDetails(doc.current, result, newStart, {
            headerTextColor: config?.printOptions.headerTextColor || [0, 0, 0],
            sectionHeaderTextColor: config?.printOptions
              .sectionHeaderTextColor || [0, 0, 0],
            tableHeaderBackgroundColor: config?.printOptions
              .tableHeaderBackgroundColor || [0, 0, 0],
            tableHeaderTextColor: config?.printOptions.tableHeaderTextColor || [
              255, 255, 255,
            ],
          });
        } catch (error) {
          console.warn("Error printing details", error, result);
        }
      }

      return doc.current?.output("arraybuffer");
    }
  }, [
    results,
    selectedPrintTemplate,
    selectedDataOption,
    getAutoTableOptions,
    getMostCurrentDetailsByPin,
    doc.current,
    config,
  ]);

  const printViewModel = useMemo(() => {
    if (!mapView || !config) return null;
    return new PrintViewModel({
      view: mapView,
      printServiceUrl: config?.printOptions.printServiceUrl,
    });
  }, [mapView, config]);

  const templateOptions = useMemo(
    () => [
      { label: "Letter (Portrait)", value: "Letter ANSI A Portrait" },
      { label: "Letter (Landscape)", value: "Letter ANSI A Landscape" },
      { label: "Legal (Portrait)", value: "A3 Portrait" },
      { label: "Legal (Landscape)", value: "A3 Landscape" },
      { label: "11 x 17 (Portrait)", value: "Tabloid ANSI B Portrait" },
      { label: "11 x 17 (Landscape)", value: "Tabloid ANSI B Landscape" },
    ],
    []
  );
  const formatOptions = useMemo(
    () => [
      { label: "PDF", value: "pdf", blobType: "application/pdf" },
      { label: "PNG32", value: "png32", blobType: "image/png" },
      { label: "PNG8", value: "png8", blobType: "image/png" },
      { label: "JPG", value: "jpg", blobType: "image/jpeg" },
      { label: "GIF", value: "gif", blobType: "image/gif" },
    ],
    []
  );

  const printTypeOptions = useMemo(
    () => [
      {
        label: "Print All",
        value: "all",
        needsResults: true,
      },
      {
        label: "Print Map Only",
        value: "map-only",
      },
      {
        label: "Print Data Only",
        value: "data-only",
        needsResults: true,
      },
    ],
    []
  );

  const dataOptions = useMemo(
    () => [
      {
        label: "Details",
        value: "details",
      },
      {
        label: "Summary",
        value: "summary",
      },
    ],
    []
  );

  const getMapPrintArrayBuffer = useCallback(async () => {
    if (!printViewModel || !config) return;
    if (esriConfig && esriConfig.request && esriConfig.request.interceptors) {
      esriConfig.request.interceptors = esriConfig.request.interceptors.filter(
        (i) => {
          if (!i.urls) return true;
          return i.urls[0] !== config.printOptions.printServiceUrl;
        }
      );
      esriConfig.request.interceptors.push({
        urls: [config.printOptions.printServiceUrl],
        before: (params) => {
          const web_Map_as_JSON = JSON.parse(
            params.requestOptions.query.Web_Map_as_JSON
          );
          web_Map_as_JSON.operationalLayers =
            web_Map_as_JSON.operationalLayers.filter(
              (l) => l.layerDefinition?.definitionExpression !== "1=0"
            );
          if (preserveScale) {
            web_Map_as_JSON.mapOptions.scale = mapView?.scale;
          } else {
            web_Map_as_JSON.mapOptions.extent = mapView?.extent;
          }

          web_Map_as_JSON.layoutOptions = {
            scaleBarOptions: {},
            legendOptions: {
              operationalLayers: web_Map_as_JSON.operationalLayers
                .filter((l) => {
                  return l.id !== "highlight-layer";
                })
                .map((l) => {
                  return { id: l.id, title: l.title };
                }),
            },
          };
          // if (!showLegend) {
          web_Map_as_JSON.layoutOptions.legendOptions = {
            operationalLayers: [],
          };
          // }
          web_Map_as_JSON.layoutOptions["titleText"] = printTitle;

          params.requestOptions.query.Web_Map_as_JSON =
            JSON.stringify(web_Map_as_JSON);
          console.log(web_Map_as_JSON);
        },
      });
    }
    const printParams = {
      format: selectedPrintFormat,
      layout: selectedPrintTemplate,
      preserveScale: preserveScale,
      preserveExtent: !preserveScale,
      layoutOptions: {
        titleText: printTitle,
      },
    };

    const printRes = await printViewModel.print(printParams);

    const raw = await fetch(printRes.url);
    const arrayBuffer = await raw.arrayBuffer();

    return arrayBuffer;
  }, [
    printViewModel,
    printTitle,
    selectedPrintTemplate,
    showLegend,
    mapView,
    config,
    selectedPrintFormat,
    preserveScale,
  ]);

  const print = async () => {
    setIsPrinting(true);
    setPrintoutUrl("");
    if (selectedPrintType === "data-only") {
      const arrayBuffer = await generatePdf();
      const url = getDataUrlFromArrayBuffer(arrayBuffer);
      setPrintoutUrl(url);
      setIsPrinting(false);
      return;
    }

    if (selectedPrintType === "map-only") {
      const mapArrayBuffer = await getMapPrintArrayBuffer();
      if (mapArrayBuffer === undefined) {
        setIsPrinting(false);
        return;
      }
      const formatBlobType = formatOptions.find(
        (f) => f.value === selectedPrintFormat
      )?.blobType;
      if (config?.printOptions.excludeDisclaimer) {
        const url = getDataUrlFromArrayBuffer(mapArrayBuffer, formatBlobType);
        setPrintoutUrl(url);
        setIsPrinting(false);
        return;
      }
      let mapArrayBufferWithDisclaimer = await addDisclaimer(mapArrayBuffer);
      if (showLegend) {
        mapArrayBufferWithDisclaimer = await addLegendImage(
          mapArrayBufferWithDisclaimer
        );
      }
      const url = getDataUrlFromArrayBuffer(mapArrayBufferWithDisclaimer);

      setPrintoutUrl(url);
      setIsPrinting(false);
      return;
    }

    if (selectedPrintType === "all") {
      const proms = [generatePdf(), getMapPrintArrayBuffer()];
      const [pdfArrayBuffer, mapArrayBuffer] = await Promise.all(proms);
      if (mapArrayBuffer === undefined || pdfArrayBuffer === undefined) {
        setIsPrinting(false);
        return;
      }
      let mapArrayBufferWithDisclaimer = await addDisclaimer(mapArrayBuffer);
      if (showLegend) {
        mapArrayBufferWithDisclaimer = await addLegendImage(
          mapArrayBufferWithDisclaimer
        );
      }
      const mergedPdf = await mergePdfs([
        mapArrayBufferWithDisclaimer,
        pdfArrayBuffer,
      ]);
      const url = getDataUrlFromArrayBuffer(mergedPdf);
      setPrintoutUrl(url);
      setIsPrinting(false);
      return;
    }
  };

  const openPrintClicked = useCallback(() => {
    if (printoutUrl === "doc") {
      doc.current.save(`parcel-${selectedDataOption}.pdf`);
    } else {
      window.open(printoutUrl, "_blank");
    }
  }, [printoutUrl, doc.current, selectedDataOption]);

  return (
    <BaseDraggable
      id={key}
      onClose={() => {
        setPrintoutUrl("");
        setIsPrinting(false);
        toggleActiveItem(key);
      }}
      visible={isActive(key)}
      initialPosition="top-right"
      headerText="PRINT"
      size="auto"
    >
      <div className="w-64">
        <p>Title:</p>
        <input
          disabled={selectedPrintType === "data-only"}
          className="input input-sm input-bordered w-full border"
          value={printTitle}
          onChange={(e) => setPrintTitle(e.target.value)}
          placeholder="Enter a title"
        />
        <p className="mt-3">Template:</p>
        <select
          className="select select-sm select-bordered w-full rounded-md"
          value={selectedPrintTemplate}
          onChange={(e) => setSelectedPrintTemplate(e.target.value)}
        >
          {templateOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {config?.printOptions.showFormatOptions && (
          <>
            <p className="mt-3">Format:</p>
            <select
              className="select select-sm select-bordered w-full rounded-md mb-2"
              value={selectedPrintFormat}
              onChange={(e) => setSelectedPrintFormat(e.target.value)}
            >
              {formatOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </>
        )}
        {config?.printOptions.showScaleExtentOption && (
          <>
            {[
              { label: "Preserve Extent", value: false },
              { label: "Preserve Scale", value: true },
            ].map((option, i) => (
              <label
                key={i}
                className="label p-0 cursor-pointer flex items-center justify-start"
              >
                <input
                  className="radio radio-xs checked:bg-blue-500 mr-2"
                  name="preserveScale"
                  type="radio"
                  value={option.value.toString()}
                  checked={preserveScale === option.value}
                  onChange={(e) => setPreserveScale(e.target.value === "true")}
                />
                <span key={option.value.toString()}>{option.label}</span>
              </label>
            ))}
          </>
        )}
        {config?.printOptions.allowDataPrint && (
          <>
            <p className="mt-3">Print Type:</p>
            {printTypeOptions.map((option, i) => (
              <label
                key={i}
                className="label p-0 cursor-pointer flex items-center justify-start"
              >
                <input
                  className="radio radio-xs checked:bg-blue-500 mr-2"
                  name="printType"
                  disabled={option.needsResults && results.length === 0}
                  type="radio"
                  value={option.value}
                  checked={selectedPrintType === option.value}
                  onChange={(e) => setSelectedPrintType(e.target.value)}
                />
                <span key={option.value}>{option.label}</span>
              </label>
            ))}
            <p className="mt-3">Data:</p>
            <select
              disabled={selectedPrintType === "map-only"}
              className="select select-sm select-bordered w-full"
              value={selectedDataOption}
              onChange={(e) => setSelectedDataOption(e.target.value)}
            >
              {dataOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </>
        )}

        {config?.printOptions.showLegendOption && (
          <>
            <p className="mt-3">Legend:</p>
            <label className="label cursor-pointer flex items-center justify-start">
              <input
                checked={showLegend}
                onChange={() => setShowLegend((prev) => !prev)}
                type="checkbox"
                className="input-xs mr-2"
              />
              <span>Include legend on printout</span>
            </label>
          </>
        )}

        <button
          disabled={isPrinting}
          className={`btn-athena mt-2 px-4 py-1 ${
            isPrinting ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={() => print()}
        >
          Print
        </button>
        {isPrinting && (
          <div className="px-2 w-full py-2 mt-3 bg-[#FCF8E3] text-[#C09853]">
            <p>Printing...</p>
          </div>
        )}

        {printoutUrl !== "" && (
          <div className="w-full px-2 py-2 mt-3 bg-[#DFF0D7] text-[#468847]">
            <p>Your printout is complete!</p>
            <button
              onClick={openPrintClicked}
              className="cursor-pointer text-blue-500 hover:underline"
            >
              Click here to open printout
            </button>
          </div>
        )}
      </div>
    </BaseDraggable>
  );
}
